import { useState, useMemo } from 'react'
import { Clock, AlertTriangle } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { DetailLevelToggle } from './DetailLevelToggle'
import { MovementCard } from './MovementCard'
import type { DetailLevel, MovementCallbacks } from './types'
import type { ScheduleItem } from './useTodayBoardData'

interface DayFlowViewProps extends MovementCallbacks {
  movements: ScheduleItem[]
}

export function DayFlowView({ movements, onRowClick, onCheckIn, onCheckOut, onRegister, onShowDetails }: DayFlowViewProps) {
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('L1')

  const nowStr = useMemo(() => {
    const now = new Date()
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
  }, [])

  // Determine the NOW marker insertion index
  const nowIndex = useMemo(() => {
    const now = new Date()
    const nowMs = now.getTime()
    for (let i = 0; i < movements.length; i++) {
      if (movements[i].sortTime && new Date(movements[i].sortTime!).getTime() > nowMs) {
        return i
      }
    }
    return movements.length
  }, [movements])

  if (movements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm py-12">
        No movements scheduled for this day
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Sticky control bar */}
      <div className="flex items-center justify-center pb-3 shrink-0">
        <DetailLevelToggle value={detailLevel} onChange={setDetailLevel} />
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="flex flex-col items-center pb-24 w-full">
          <div className="relative w-full max-w-4xl">
            {/* Center spine */}
            <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-0.5 bg-border" />

            <div className="w-full relative flex flex-col gap-10 py-4">
              {movements.map((m, idx) => (
                <div key={`${m.movementType}-${m.allocationId}`} className="relative w-full flex flex-col justify-center">
                  {/* NOW marker */}
                  {idx === nowIndex && <NowMarker time={nowStr} />}

                  {/* Crew conflict banner (mock — between items 3 and 4) */}
                  {idx === 3 && <CrewConflictBanner />}

                  <DayFlowRow
                    item={m}
                    isPast={idx < nowIndex}
                    detailLevel={detailLevel}
                    onRowClick={onRowClick}
                    onCheckIn={onCheckIn}
                    onCheckOut={onCheckOut}
                    onRegister={onRegister}
                    onShowDetails={onShowDetails}
                  />
                </div>
              ))}

              {/* NOW marker at end if all items are past */}
              {nowIndex === movements.length && <NowMarker time={nowStr} />}

              {/* End of Day */}
              <div className="relative w-full flex justify-center mt-8">
                <div className="bg-card border-2 rounded-full px-5 py-2 text-xs font-bold tracking-widest text-muted-foreground uppercase shadow-sm z-10 flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5" /> End of Day
                </div>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  )
}

// ── Sub-components ──────────────────────────────────

function NowMarker({ time }: { time: string }) {
  return (
    <div className="relative flex items-center z-10 my-4">
      <div className="h-px bg-blue-500 flex-1" />
      <div className="px-3 py-1 rounded-full bg-blue-500 text-white text-[10px] font-bold tracking-widest flex items-center gap-1.5 shadow-sm">
        <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
        NOW {time}
      </div>
      <div className="h-px bg-blue-500 flex-1" />
    </div>
  )
}

function CrewConflictBanner() {
  return (
    <div className="flex justify-center my-2 z-20">
      <div className="bg-amber-50 border border-amber-200 shadow-md rounded-lg p-2 text-center flex flex-col items-center">
        <div className="flex items-center gap-1.5 text-amber-700 mb-0.5">
          <AlertTriangle className="h-3.5 w-3.5 animate-pulse" />
          <span className="font-bold text-[10px] tracking-wider uppercase">Crew Conflict</span>
        </div>
        <div className="text-amber-800 font-medium text-xs">14:00–15:00 · 4 ops, 2 stacking</div>
      </div>
    </div>
  )
}

function DayFlowRow({
  item,
  isPast,
  detailLevel,
  onRowClick,
  onCheckIn,
  onCheckOut,
  onRegister,
  onShowDetails,
}: {
  item: ScheduleItem
  isPast: boolean
  detailLevel: DetailLevel
  onRowClick?: MovementCallbacks['onRowClick']
  onCheckIn?: MovementCallbacks['onCheckIn']
  onCheckOut?: MovementCallbacks['onCheckOut']
  onRegister?: MovementCallbacks['onRegister']
  onShowDetails?: MovementCallbacks['onShowDetails']
}) {
  const isArrival = item.movementType === 'arriving'

  // Time label from sortTime
  const timeLabel = useMemo(() => {
    if (item.scheduledTime) {
      const d = new Date(item.scheduledTime)
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
    return null
  }, [item.scheduledTime])

  return (
    <div className="relative w-full flex items-center justify-center">
      {/* Node marker on spine */}
      <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[11px] h-[11px] rounded-full bg-muted-foreground border-[2.5px] border-background z-20 shadow-sm" />

      {/* Left side (arrivals) */}
      <div className="w-1/2 pr-10 flex justify-end relative items-center">
        {isArrival && (
          <>
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-10 border-t-2 border-border pointer-events-none" />
            {timeLabel && (
              <div className="absolute right-[55%] top-1/2 -translate-y-1/2 text-xs font-bold font-mono text-muted-foreground bg-card px-1.5 py-0.5 rounded shadow-sm border">
                {timeLabel}
              </div>
            )}
            <div className={`w-[340px] relative z-10 ${isPast ? 'opacity-60 hover:opacity-100 transition-opacity' : ''}`}>
              <MovementCard
                item={item}
                detailLevel={detailLevel}
                compact
                onRowClick={onRowClick}
                onCheckIn={onCheckIn}
                onCheckOut={onCheckOut}
                onRegister={onRegister}
                onShowDetails={onShowDetails}
              />
            </div>
          </>
        )}
      </div>

      {/* Right side (departures) */}
      <div className="w-1/2 pl-10 flex justify-start relative items-center">
        {!isArrival && (
          <>
            <div className="absolute left-0 top-1/2 -translate-y-1/2 w-10 border-t-2 border-border pointer-events-none" />
            {timeLabel && (
              <div className="absolute left-[55%] top-1/2 -translate-y-1/2 text-xs font-bold font-mono text-muted-foreground bg-card px-1.5 py-0.5 rounded shadow-sm border">
                {timeLabel}
              </div>
            )}
            <div className={`w-[340px] relative z-10 ${isPast ? 'opacity-60 hover:opacity-100 transition-opacity' : ''}`}>
              <MovementCard
                item={item}
                detailLevel={detailLevel}
                compact
                onRowClick={onRowClick}
                onCheckIn={onCheckIn}
                onCheckOut={onCheckOut}
                onRegister={onRegister}
                onShowDetails={onShowDetails}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
